/**
 * MapGridButtons — the standard New / Open / Save / Export action grid, with a
 * full-width Clear beneath it.
 *
 * Shared by every editor's bottom bar so the layout, icons, names and styling
 * stay identical: a 2×2 grid of stacked icon-above-label buttons (the same
 * "Camera View" card style) — New · Open · Save · Export — then Clear spanning
 * the full width below. Each editor passes its own handlers; Save persists the
 * project only (no download), Export writes the shareable .synth/.flow file.
 */

import React from 'react';
import { Tooltip, tip } from './Tooltip';
import { IcSparkle, IcFolder, IcSave, IcUpload, IcTrash } from './icons';
import { cameraGridStyle, cameraBtnStyle, mapBtnVariant, clearBtnVariant } from './editorSharedStyles';

interface Props {
  /** Omit to render the New cell disabled (e.g. an editor without a new-map flow). */
  onNew?: () => void;
  onOpen: () => void;
  onSave: () => void;
  /** Export the shareable file (.synth/.flow), optionally bundling a visualizer. */
  onExport: () => void;
  onClear: () => void;
  saveDisabled?: boolean;
  exportDisabled?: boolean;
  clearDisabled?: boolean;
}

const cell = { ...cameraBtnStyle, width: '100%' } as React.CSSProperties;
const labelStyle: React.CSSProperties = { fontSize: 9 };
// White icons for the non-destructive actions (the accent tint read as cyan).
const whiteIcon: React.CSSProperties = { color: '#fff' };
// Clear is the destructive action — its icon is clearly red.
const redIcon: React.CSSProperties = { color: '#EF4444' };
// Clear now spans the full width beneath the 2×2 grid; the row is horizontal
// (icon beside label) so it reads as one wide bar.
const clearRowStyle: React.CSSProperties = {
  ...cameraBtnStyle, width: '100%', flexDirection: 'row', gap: 7, marginTop: 6,
};

export default function MapGridButtons({
  onNew, onOpen, onSave, onExport, onClear, saveDisabled, exportDisabled, clearDisabled,
}: Props) {
  return (
    <div>
      <div style={cameraGridStyle}>
        <Tooltip content={tip({ title: 'New Map', body: 'Start a fresh map from scratch — pick the audio, song info and cover.', shortcut: 'Ctrl+N' })}>
          <button style={{ ...cell, ...mapBtnVariant, opacity: onNew ? 1 : 0.5 }} onClick={onNew} disabled={!onNew}>
            <IcSparkle size={14} style={whiteIcon} />
            <span style={labelStyle}>New</span>
          </button>
        </Tooltip>
        <Tooltip content={tip({ title: 'Open Map', body: 'Load an existing map from your library.', shortcut: 'Ctrl+O' })}>
          <button style={{ ...cell, ...mapBtnVariant }} onClick={onOpen}>
            <IcFolder size={14} style={whiteIcon} />
            <span style={labelStyle}>Open</span>
          </button>
        </Tooltip>
        <Tooltip content={tip({ title: 'Save', body: 'Save your project changes (like autosave, but manual). Does NOT download a file.', shortcut: 'Ctrl+S' })}>
          <button style={{ ...cell, ...mapBtnVariant, opacity: saveDisabled ? 0.5 : 1 }} onClick={onSave} disabled={saveDisabled}>
            <IcSave size={14} style={whiteIcon} />
            <span style={labelStyle}>Save</span>
          </button>
        </Tooltip>
        <Tooltip content={tip({ title: 'Export', body: 'Write the shareable map file (.synth/.flow) — optionally bundling a visualizer.' })}>
          <button style={{ ...cell, ...mapBtnVariant, opacity: exportDisabled ? 0.5 : 1 }} onClick={onExport} disabled={exportDisabled}>
            <IcUpload size={14} style={whiteIcon} />
            <span style={labelStyle}>Export</span>
          </button>
        </Tooltip>
      </div>
      <Tooltip content={tip({ title: 'Clear Map', body: 'Remove the notes from the chart. The audio and metadata stay.' })}>
        <button style={{ ...clearRowStyle, ...clearBtnVariant, opacity: clearDisabled ? 0.5 : 1 }} onClick={onClear} disabled={clearDisabled} data-hover-danger>
          <IcTrash size={14} className="ic-danger" style={redIcon} />
          <span style={labelStyle}>Clear</span>
        </button>
      </Tooltip>
    </div>
  );
}
